import { Component, OnInit } from '@angular/core';
import {ShippingAddress} from '../../_models/shipping-address';
import {Title} from '@angular/platform-browser';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {User} from '../../_models/user';
import {UserService} from '../../_services/user.service';
import { CountryService } from '../../country/country.service';
import { Country } from '../../country/Country';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ShippingMeanService } from '../../_services/shipping-mean.service';

@Component({
    selector: 'app-shipping-address',
    templateUrl: './shipping-address.component.html',
    styleUrls: ['./shipping-address.component.css'],
    standalone: false
})
export class ShippingAddressComponent implements OnInit {

  shippingAddresses: ShippingAddress[] = [];
  loading = true;
  showForm = false;
  currentUser: User;
  shippingAddressForm: UntypedFormGroup;
  saving = false;
  deleting = false;
  makingPrimary = false;
  countries: Country[] = [];
  selectedCountry: Country | null = null;
  cities: any[] = [];

  constructor(public userService: UserService, public titleService: Title,
    private fb: UntypedFormBuilder, private translateService: TranslateService,
    private countryService: CountryService, private toastr: ToastrService,
    private shippingMeanService: ShippingMeanService) {
    
      this.translateService.get("rightnav.my_account").subscribe(value => {
        this.titleService.setTitle(value);
      })

    this.currentUser = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
  }

  ngOnInit() {
    this.loadCountries();
    this.loadCities();
    this.loadingShippingAddress();
  }

  loadCities() {
    const userCountryId = typeof this.currentUser.country === 'object' ? this.currentUser.country?.id : this.currentUser.country;
    if (userCountryId) {
      this.shippingMeanService.getCitiesByCountry(Number(userCountryId)).subscribe({
        next: (response: any) => {
          this.cities = response?.data || response || [];
        },
        error: () => {
          this.cities = [];
        }
      });
    }
  }

  loadCountries() {
    this.countryService.loadAll().subscribe({
      next: (data) => {
        this.countries = data;
      },
      error: () => {
        this.countries = [];
      }
    });
  }

  onCountryChange(event: any) {
    const id = +event.target.value;
    this.selectedCountry = this.countries.find(c => c.id === id) ?? null;
    if (id) {
      this.shippingMeanService.getCitiesByCountry(id).subscribe({
        next: (response: any) => {
          this.cities = response?.data || response || [];
        },
        error: () => {
          this.cities = [];
        }
      });
    } else {
      this.cities = [];
    }
  }

  getCountryName(id: number): string {
    const country = this.countries.find(c => c.id === id);
    return country ? `${country.iso}` : '';
  }

  getCountryFlag(id: number): string {
    const country = this.countries.find(c => c.id === id);
    return country ? `https://flagcdn.com/16x12/${country.iso?.toLowerCase()}.png` : '';
  }

  loadingShippingAddress() {
    this.userService.loadShippingAddresses(this.currentUser.id).subscribe(
        (response: any) => {
          let shippingAddresses = response.data || response || [];
          // Normalize API response: map country.id to countryId
          shippingAddresses = shippingAddresses.map((addr: any) => ({
            ...addr,
            countryId: addr.country?.id || addr.countryId || 37
          }));
          this.shippingAddresses = shippingAddresses;
          this.currentUser.shippingAddressList = shippingAddresses;
          this.userService.setCurrentUser(this.currentUser);
          this.loading = false;
        }
    );
  }

  addShippingAddress() {
    this.generateForm(null);
    this.showForm = true;
  }

  generateForm(shippingAddress: ShippingAddress) {
    const defaultCountryId = shippingAddress ? shippingAddress.countryId : 37;
    this.shippingAddressForm = this.fb.group({
      id: new UntypedFormControl(shippingAddress ? shippingAddress.id : 0),
      name: new UntypedFormControl(shippingAddress ? shippingAddress.name : '', Validators.required),
      region: new UntypedFormControl(shippingAddress ? shippingAddress.region : ''),
      status: new UntypedFormControl(shippingAddress ? shippingAddress.status : ''),
      town: new UntypedFormControl(shippingAddress ? shippingAddress.town : '', Validators.required),
      countryId: new UntypedFormControl(defaultCountryId, Validators.required),
      addressLine1: new UntypedFormControl(shippingAddress ? shippingAddress.addressLine1 : '', Validators.required),
      addressLine2: new UntypedFormControl(shippingAddress ? shippingAddress.addressLine2 : ''),
    });
    this.selectedCountry = this.countries.find(c => c.id === defaultCountryId) ?? null;
  }

  editShippingAddress(shippingAddress: ShippingAddress) {
    this.generateForm(shippingAddress);
    this.showForm = true;
  }

  saveShippingAddress() {
    if (!this.shippingAddressForm.dirty ||  this.shippingAddressForm.untouched || this.shippingAddressForm.invalid) {
      return;
    }
    this.saving = true;
    const currentUser = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    if ( this.shippingAddressForm.value.id === 0 ) {
      this.shippingAddressForm.removeControl('status');
    }
    this.userService.saveShippingAddress(currentUser.id, this.shippingAddressForm.value).subscribe(
        () => {
          this.showForm = false;
          this.loading = true;
          this.shippingAddressForm.reset();
          this.loadingShippingAddress();
        },
        () => { }
    );
    this.saving = false;
  }

  confirmDeletion(address: ShippingAddress) {
    this.showForm = false;
    document.getElementById('sphAddr' + address.id)?.classList.remove('delete-message');
  }

  cancelDeletion(address: ShippingAddress) {
    document.getElementById('sphAddr' + address.id)?.classList.add('delete-message');
  }

  deleteShippingAddress(shippingAddress: ShippingAddress) {
    this.deleting = true;
    this.userService.deleteShippingAddress(shippingAddress).subscribe(
        () => {
          this.loading = true;
          this.loadingShippingAddress();
        },
        () => { this.deleting = false; }
    );
  }

  get f() { return this.shippingAddressForm.controls; }

  closeForm() {
    this.showForm = false;
  }

  confirmMakePrimary(address: ShippingAddress) {
    this.showForm = false;
    document.getElementById('makePrimary' + address.id)?.classList.remove('make-primary-message');
  }

  cancelMakePrimary(address: ShippingAddress) {
    document.getElementById('makePrimary' + address.id)?.classList.add('make-primary-message');
  }

  makePrimary(shippingAddress: ShippingAddress) {
    this.makingPrimary = true;
    const payload = {
      id: shippingAddress.id,
      name: shippingAddress.name,
      addressLine1: shippingAddress.addressLine1,
      addressLine2: shippingAddress.addressLine2,
      region: shippingAddress.region,
      town: shippingAddress.town,
      status: 'primary',
      country: {
        id: shippingAddress.countryId
      }
    };
    this.userService.updateShippingAddress(shippingAddress.id, payload).subscribe(
      () => {
        this.makingPrimary = false;
        this.loading = true;
        this.toastr.success('Address set as primary successfully');
        this.loadingShippingAddress();
      },
      () => {
        this.makingPrimary = false;
        this.toastr.error('Failed to set address as primary');
      }
    );
  }
}
